import React, {Component} from 'react';
import {connect} from 'react-redux';
import {TextField} from '@material-ui/core';
import NodesTable from './NodesTable';
import ConnectionsTable from './ConnectionsTable';
import ElementsTable from './ElementsTable';
import MappingsTable from './MappingsTable';
import './lineage-table.scss';

/**
 * Build a flat id→label lookup covering nodes, tables, and elements.
 * Recomputed only when map changes (called inside render from memoised field).
 */
const buildNameIndex = (map) => {
  const idx = {};
  (map.nodes || []).forEach((n) => {
    idx[n.id] = n.data.label || 'Untitled';
    (n.data.tables || []).forEach((t) => {
      idx[t.id] = t.name || t.id;
      (t.dataElements || []).forEach((el) => {
        idx[el.id] = el.displayName || el.name || el.id;
      });
    });
    (n.data.dataElements || []).forEach((el) => {
      idx[el.id] = el.displayName || el.name || el.id;
    });
  });
  return idx;
};

/**
 * Read-only tabular view of a lineage diagram (nodes/edges/elements/mappings).
 * Editing is intentionally graph-view-only — the tables never expose create/update/
 * delete affordances (`editable` is always false), so this component is pure display
 * + a client-side text filter. Switch to Graph view to edit.
 */
class LineageTableView extends Component {
  constructor(props) {
    super(props);
    this.state = {filter: ''};
    this._nameIndex = null;
    this._lastMap = null;
  }

  _getNameIndex() {
    const {map} = this.props;
    if (map !== this._lastMap) {
      this._nameIndex = buildNameIndex(map);
      this._lastMap = map;
    }
    return this._nameIndex;
  }

  _appNodes() {
    return (this.props.map.nodes || []).filter((n) => n.kind === 'app');
  }

  _characteristicIndex() {
    const idx = {};
    (this.props.characteristics || []).forEach((c) => {
      idx[c.id] = c.name;
    });
    return idx;
  }

  _groupIndex() {
    const idx = {};
    (this.props.map.nodes || []).filter((n) => n.kind === 'group').forEach((g) => {
      idx[g.id] = g.data.label || 'Group';
    });
    return idx;
  }

  _filterRows(rows) {
    const q = this.state.filter.toLowerCase().trim();
    if (!q) return rows;
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }

  _buildElementRows() {
    const appNodes = this._appNodes();
    const rows = [];
    appNodes.forEach((n) => {
      const nodeLabel = n.data.label || 'Untitled';
      (n.data.tables || []).forEach((t) => {
        (t.dataElements || []).forEach((el) => {
          rows.push({...el, _key: `${n.id}::${t.id}::${el.id}`, nodeId: n.id, nodeLabel, tableId: t.id, tableName: t.name});
        });
      });
      (n.data.dataElements || []).forEach((el) => {
        rows.push({...el, _key: `${n.id}::${el.id}`, nodeId: n.id, nodeLabel, tableId: null, tableName: null});
      });
    });
    return rows;
  }

  _buildMappingPartitions() {
    const {map} = this.props;
    const crossRows = [];
    const intraByNode = {};
    (map.mappings || []).forEach((m) => {
      if (m.sourceNodeId === m.targetNodeId) {
        if (!intraByNode[m.sourceNodeId]) intraByNode[m.sourceNodeId] = [];
        intraByNode[m.sourceNodeId].push(m);
      } else {
        crossRows.push(m);
      }
    });
    return {crossRows, intraByNode};
  }

  render() {
    const {map, lineageViewType} = this.props;
    const {filter} = this.state;

    // Editing is graph-view-only: these tables are pure read-only display.
    const nameIndex = this._getNameIndex();
    const charIdx = this._characteristicIndex();
    const groupIdx = this._groupIndex();
    const appNodes = this._appNodes();

    const viewLabel = lineageViewType === 'system' ? 'System lineage' : 'Data lineage';

    const nodeRows = this._filterRows(appNodes);
    const edgeRows = this._filterRows(map.edges || []);
    const elRows = this._filterRows(this._buildElementRows());
    const {crossRows, intraByNode} = this._buildMappingPartitions();
    const filteredCrossRows = this._filterRows(crossRows);
    const filteredIntraByNode = {};
    Object.keys(intraByNode).forEach((nodeId) => {
      const filtered = this._filterRows(intraByNode[nodeId]);
      if (filtered.length > 0) filteredIntraByNode[nodeId] = filtered;
    });

    return (
      <div className="lt-table-view" data-testid="lt-table-view">
        <div className="lt-topbar">
          <span className="lt-view-label">{viewLabel}</span>
          <span className="lt-readonly-hint" data-testid="lt-readonly-hint">
            Read-only — switch to Graph view to edit
          </span>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => this.setState({filter: e.target.value})}
            className="lt-filter-input"
            inputProps={{'data-testid': 'lt-filter'}}
          />
        </div>

        {lineageViewType === 'system' ? (
          <>
            <NodesTable
              rows={nodeRows}
              characteristicIndex={charIdx}
              groupIndex={groupIdx}
            />
            <ConnectionsTable
              rows={edgeRows}
              labelIndex={nameIndex}
            />
          </>
        ) : (
          <>
            <ElementsTable rows={elRows} />
            <MappingsTable
              crossRows={filteredCrossRows}
              intraByNode={filteredIntraByNode}
              nameIndex={nameIndex}
            />
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  map: state.lineageReducer.map,
  lineageViewType: state.lineageReducer.lineageViewType,
  characteristics: state.lineageReducer.characteristics,
});

export default connect(mapStateToProps)(LineageTableView);
